import React from "react";
import { ICourseSummary } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";
import { helperService } from "src/services/helper.service";

import PointRedemption from "src/components/PointRedemption/PointRedemption";
import CartTotals, { ICartTotalLine } from "src/components/CartTotals/CartTotals";

import styles from "./CourseCart.module.scss";

interface IProps {
  className?: string,
  innerClassName?: string,
  course: ICourseSummary | null,
  cart: any,
  onBehalfEmail?: string,
  onBehalfName?: string,
  onBehalfId?: string,
  showPoints?: boolean,
  onUpdatePoints(points: number): void
}

const CourseCart: React.FC<IProps> = (props) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: props.cart?.currency.toUpperCase() || "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  const user = helperService.getUser();
  const isSeminar = props.course?.course_type === Constants.course_type.seminar;
  
  // For seminars, determine what to display based on whether it's on-behalf-of
  const getDisplayInfo = () => {
    if (!isSeminar) return { name: '', email: '' };
    
    if (props.onBehalfEmail) {
      // On-behalf-of: use the provided name and email
      return {
        name: props.onBehalfName || props.onBehalfEmail,
        email: props.onBehalfEmail
      };
    }
    
    // Regular user registration
    return {
      name: user.display_name,
      email: user.email
    };
  };
  
  const displayInfo = getDisplayInfo();

  const splitSessions = (sessions: { waitlist?: boolean }[] = []) => ({
    open: sessions.filter((session) => !session.waitlist),
    waiting: sessions.filter((session) => session.waitlist),
  });

  const hasSessionFees = () => {
    if (props.course?.schedule && props.course?.schedule?.length > 0 && props.course?.schedule[0].fee !== undefined) {
      return props.course?.schedule[0].fee;
    }

    return false;
  }

  const getSessionFee = () => {
    return <>
      <span>{formatter.format(hasSessionFees() || 0)}</span>
      <span className={styles.feeLabel}>per session</span>
    </>;
  }

  const redeemableAmount = () => {
    const details = props.cart?.details || [];
    const sessionTotal = details.reduce((total: number, detail: any) => {
      const sessions = detail.sessions || [];
      return total + sessions.reduce((sum: number, session: any) => sum + (Number(session.fee) || 0), 0);
    }, 0);
    if (sessionTotal > 0) {
      return sessionTotal;
    }
    return props.cart?.base_amount || props.cart?.total_amount || 0;
  };

  const courseTotalLines = (): ICartTotalLine[] => {
    const pointsDollars = props.cart?.club_points_discount_amount || 0;
    const codeDiscount = Math.max(0, Math.round(((props.cart?.discount || 0) - pointsDollars) * 100) / 100);
    const subtotal = redeemableAmount() || ((props.cart?.total_amount || 0) + (props.cart?.discount || 0));
    const lines: ICartTotalLine[] = [
      { label: "Subtotal", value: formatter.format(subtotal) },
    ];
    if (codeDiscount > 0) {
      lines.push({ label: "Discount", value: `-${formatter.format(codeDiscount)}` });
    }
    if ((props.cart?.club_points_redeemed || 0) > 0 || pointsDollars > 0) {
      lines.push({
        label: <>Points Redeemed (<strong>{(props.cart?.club_points_redeemed || 0).toLocaleString()}</strong>)</>,
        value: pointsDollars > 0 ? `-${formatter.format(pointsDollars)}` : undefined,
      });
    }
    return lines;
  };

  return (
    <div className={`${styles.CourseCart} ${props.className ? props.className : ''}`}>
      <div className={`${styles.inner} ${props.innerClassName ? props.innerClassName : ''}`}>
        <div className={styles.title}>Cart</div>
        {props.cart ? (
          <div className={`${styles.CartDetails} ${props.showPoints ? styles.withPoints : ""}`}>
            <div className={styles.CartItems}>
              {isSeminar ? (
                // For seminars, show user info instead of dog info
                (() => {
                  const { open, waiting } = splitSessions(props.cart.details[0]?.sessions);
                  const fullyWaitlisted = open.length === 0 && waiting.length > 0;
                  return (
                    <div className={styles.CartItem}>
                      <div className={styles.ItemInfo}>
                        <div className={styles.dogName}>
                          <div className={styles.dogNameWrap}>
                            <span className={styles.callName}>{displayInfo.name}</span>
                            {displayInfo.email && <span className={styles.regName}>{displayInfo.email}</span>}
                          </div>
                        </div>
                        <div className={styles.courseFee}>
                          {fullyWaitlisted ? <span className={styles.waitlisted}>Waitlisted</span> : (hasSessionFees() ? getSessionFee() : formatter.format(props.course?.fee || 0))}
                        </div>
                      </div>
                      <div className={styles.sessions}>
                        {(fullyWaitlisted ? waiting.length : open.length)} Sessions
                        {waiting.length > 0 && !fullyWaitlisted && <div className={styles.waitlisted}>{waiting.length} Waitlisted</div>}
                      </div>
                    </div>
                  );
                })()
              ) : (
                // For regular courses, show dog info
                props.cart.details.map((detail: any, index: number) => {
                  const { open, waiting } = splitSessions(detail.sessions);
                  const fullyWaitlisted = open.length === 0 && waiting.length > 0;
                  return (
                    <div key={index} className={styles.CartItem}>
                      <div className={styles.ItemInfo}>
                        <div className={styles.dogName}>
                          <div className={styles.dogNameWrap}>
                            <span className={styles.callName}>{detail.call_name}</span>
                            <span className={styles.regName}>{detail.registered_name}</span>
                          </div>
                        </div>
                        <div className={styles.courseFee}>
                          {fullyWaitlisted ? <span className={styles.waitlisted}>Waitlisted</span> : (hasSessionFees() ? getSessionFee() : formatter.format(props.course?.fee || 0))}
                        </div>
                      </div>
                      <div className={styles.sessions}>
                        {(fullyWaitlisted ? waiting.length : open.length)} Sessions
                        {waiting.length > 0 && !fullyWaitlisted && <div className={styles.waitlisted}>{waiting.length} Waitlisted</div>}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        ) : (
          <div className={styles.EmptyCart}>
            <p className={styles.empty}>
              {isSeminar 
                ? "You haven't selected any sessions, yet." 
                : "You haven't added any dogs to the course, yet."
              }
            </p>
          </div>
        )}
        {props.cart && <div className={styles.total}>
          {props.showPoints && props.course && (
            <div className={styles.points}>
              <PointRedemption
                clubId={props.course.club._id}
                behalfUserId={props.onBehalfId}
                redeemableAmount={redeemableAmount()}
                onRedeem={props.onUpdatePoints}
              />
            </div>
          )}
          <CartTotals
            lines={courseTotalLines()}
            totalLabel="Total"
            totalValue={formatter.format(props.cart.total_amount || 0)}
          />
        </div>}
      </div>
    </div>
  );
};

export default CourseCart;